import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polygon, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import {
  MdAdd,
  MdArrowDownward,
  MdArrowUpward,
  MdDelete,
  MdLocationPin,
  MdWarning,
  MdSatelliteAlt,
  MdMap,
} from "react-icons/md";
import MobileInterface from "../MobileInterface";

/* Leaflet icon fix */
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { AppContext } from "../App";
import { TiWarning } from "react-icons/ti";
import CoordinateCards from "../components/CoordinateCards";
import InquiryGeometryMap from "../components/InquiryGeometryMap";
import {
  ARC1960_DATUM,
  createPointsCrsMetadata,
  MANUAL_IMPORTATION,
  MOBILE_GPS_MAPPING,
  normalizeGeographicPointsToWgs84,
  WGS84_DATUM,
} from "../CoordinateSystems";
import {
  STR_ADD_POINT,
  STR_ARC1960_WARNING,
  STR_COORDINATE_DATUM,
  STR_DECIMAL_COORDINATE_HELP,
  STR_LATITUDE,
  STR_LONGITUDE,
  STR_MANUAL_IMPORTATION,
  STR_MANUAL_POINTS_INVALID,
  STR_MAPPING_METHOD,
  STR_MOBILE_GPS_MAPPING,
  STR_MOVE_DOWN,
  STR_MOVE_UP,
  STR_ORIGINAL_UTM_ZONE,
  STR_REMOVE,
  STR_SOURCE_REFERENCE,
} from "../Strings";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/* Config */
const DEFAULT_MAX_GPS_ACCURACY_METERS = 200;
const DEFAULT_CENTER = { lat: -6.369028, lng: 34.888822 };
const LOCATION_POLL_MS = 1500;

function toRad(value) {
  return value * Math.PI / 180;
}

/**
 * Geodesic polygon area (spherical approximation, WGS84 radius)
 * Returns square meters.
 */
function getGeodesicAreaSqm(points) {
  if (!points || points.length < 3) return 0;

  const R = 6378137; // meters
  let area = 0;

  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];

    const lat1 = toRad(p1.lat);
    const lat2 = toRad(p2.lat);
    const lng1 = toRad(p1.lng);
    const lng2 = toRad(p2.lng);

    area += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
  }

  area = area * R * R / 2;
  return Math.abs(area);
}

function isManualPointValid(point) {
  const lat = Number(point.lat);
  const lng = Number(point.lng);

  return (
    point.lat !== "" &&
    point.lng !== "" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

function isSamePoint(firstPoint, secondPoint) {
  return (
    firstPoint &&
    secondPoint &&
    Number(firstPoint.lat) === Number(secondPoint.lat) &&
    Number(firstPoint.lng) === Number(secondPoint.lng)
  );
}

function RecenterMap({ position, zoom = 18 }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView([position.lat, position.lng], zoom, { animate: true });
    }
  }, [map, position, zoom]);

  return null;
}

export default function LandMarker({ title, callback }) {

  const appContext = useContext(AppContext);
  const pollingRef = useRef(null);
  const manualPointIdRef = useRef(0);
  const pointsRef = useRef([]);

  const historyRef = useRef([]);
  const redoRef = useRef([]);

  const [ready, setReady] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [satellite, setSatellite] = useState(false);

  const [currentPosition, setCurrentPosition] = useState(null);
  const [accuracy, setAccuracy] = useState(null);

  const [points, setPoints] = useState([]);
  const [mappingMethod, setMappingMethod] = useState(MOBILE_GPS_MAPPING);
  const [manualDatum, setManualDatum] = useState(WGS84_DATUM);
  const [manualPointInputs, setManualPointInputs] = useState([]);
  const [originalUtmZone, setOriginalUtmZone] = useState("");
  const [sourceReference, setSourceReference] = useState("");

  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("warning"); // warning | error | info

  function showMessage(text, type = "warning") {
    setMessage(text);
    setMessageType(type);
  }

  function dismissMessage() {
    setMessage(null);
  }

  function pushHistory(prevPoints) {
    historyRef.current.push(prevPoints);
    redoRef.current = [];
  }

  function updatePoints(nextPoints) {
    pointsRef.current = nextPoints;
    setPoints(nextPoints);
  }

  function isGpsPointAlreadySelected(point) {
    return pointsRef.current.some((selectedPoint) => isSamePoint(selectedPoint, point));
  }

  async function fetchLocationOnce() {
    const res = await MobileInterface.requestCurrentLocation();
    console.log("Requested current location Str", res);

    if (!res || res === "unavailable") {
      setCurrentPosition(null);
      setAccuracy(null);
      return;
    }

    try {
      const obj = JSON.parse(res);

      if (obj && obj.lat && obj.lng) {
        setCurrentPosition({
          lat: Number(Number(obj.lat).toFixed(7)),
          lng: Number(Number(obj.lng).toFixed(7)),
        });

        if (obj.accuracy !== undefined && obj.accuracy !== null) {
          setAccuracy(Math.round(Number(obj.accuracy)));
        } else {
          setAccuracy(null);
        }
      }
    } catch (e) {
      setCurrentPosition(null);
      setAccuracy(null);
    }
  }

  async function startTracking() {
    dismissMessage();

    // Permission check (native)
    const hasPermission = await MobileInterface.checkLocationPermission();
    const enabled = await MobileInterface.isLocationEnabled();

    if (!enabled) {
      showMessage("Location is turned off. Please enable GPS.", "warning");
      MobileInterface.showLocationSettingsAlert();
      return;
    }

    if (!hasPermission) {
      showMessage("Location permission is required. Please allow it.", "warning");
      MobileInterface.requestLocationPermission();
      return;
    }

    setTracking(true);
    await fetchLocationOnce();

    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      await fetchLocationOnce();
    }, LOCATION_POLL_MS);
  }

  function stopTracking() {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = null;
    setTracking(false);
  }

  function markCorner() {
    if (!currentPosition) {
      showMessage("Waiting for GPS fix…", "info");
      return;
    }

    if (accuracy === null) {
      showMessage("Waiting for accuracy…", "info");
      return;
    }

    if (accuracy > maxGpsAccuracyMeters) {
      showMessage(
        `Accuracy too low (±${accuracy}m). Need ≤ ${maxGpsAccuracyMeters}m to mark a corner.`,
        "warning"
      );
      return;
    }

    if (isGpsPointAlreadySelected(currentPosition)) {
      showMessage("This GPS coordinate has already been selected.", "warning");
      return;
    }

    const previousPoints = pointsRef.current;
    pushHistory(previousPoints);
    updatePoints([...previousPoints, currentPosition]);
  }

  function undo() {
    if (historyRef.current.length === 0) return;

    redoRef.current.push(points);
    const prev = historyRef.current.pop();
    updatePoints(prev);
  }

  function redo() {
    if (redoRef.current.length === 0) return;

    historyRef.current.push(points);
    const next = redoRef.current.pop();
    updatePoints(next);
  }

  function clearAll() {
    if (points.length === 0) return;

    pushHistory(points);
    updatePoints([]);
  }

  function changeMappingMethod(method) {
    stopTracking();
    historyRef.current = [];
    redoRef.current = [];
    updatePoints([]);
    setManualPointInputs([]);
    setMappingMethod(method);
    dismissMessage();
  }

  function addManualPoint() {
    manualPointIdRef.current += 1;
    setManualPointInputs((previous) => [
      ...previous,
      {
        id: manualPointIdRef.current,
        lat: "",
        lng: "",
      },
    ]);
  }

  function updateManualPoint(id, key, value) {
    setManualPointInputs((previous) => previous.map((point) => (
      point.id === id ? { ...point, [key]: value } : point
    )));
  }

  function removeManualPoint(id) {
    setManualPointInputs((previous) => previous.filter((point) => point.id !== id));
  }

  function moveManualPoint(index, direction) {
    setManualPointInputs((previous) => {
      const next = [...previous];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) {
        return previous;
      }

      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  }

  const manualOriginalPoints = useMemo(() => (
    manualPointInputs
      .filter(isManualPointValid)
      .map((point) => ({
        lat: Number(point.lat),
        lng: Number(point.lng),
      }))
  ), [manualPointInputs]);

  const manualPointsValid = useMemo(() => {
    if (manualPointInputs.length < 2 || manualOriginalPoints.length !== manualPointInputs.length) {
      return false;
    }

    const uniquePoints = new Set(
      manualOriginalPoints.map((point) => `${point.lat},${point.lng}`)
    );
    return uniquePoints.size === manualOriginalPoints.length;
  }, [manualOriginalPoints, manualPointInputs.length]);

  const pointsCrsMetadata = useMemo(() => createPointsCrsMetadata({
    mappingMethod,
    sourceDatum: mappingMethod === MANUAL_IMPORTATION ? manualDatum : WGS84_DATUM,
    originalUtmZone,
    sourceReference,
  }), [mappingMethod, manualDatum, originalUtmZone, sourceReference]);

  useEffect(() => {
    if (mappingMethod === MANUAL_IMPORTATION) {
      try {
        updatePoints(normalizeGeographicPointsToWgs84(manualOriginalPoints, manualDatum));
      } catch (error) {
        updatePoints([]);
      }
    }
  }, [mappingMethod, manualDatum, manualOriginalPoints]);

  const perimeterMeters = useMemo(() => {
    if (points.length < 2) return 0;

    if (points.length === 2) {
      return L.latLng(points[0].lat, points[0].lng)
        .distanceTo(L.latLng(points[1].lat, points[1].lng));
    }

    let total = 0;
    for (let i = 0; i < points.length; i++) {
      const a = points[i];
      const b = points[(i + 1) % points.length];
      total += L.latLng(a.lat, a.lng).distanceTo(L.latLng(b.lat, b.lng));
    }
    return total;
  }, [points]);

  const areaSqm = useMemo(() => {
    return getGeodesicAreaSqm(points);
  }, [points]);

  const areaHectares = useMemo(() => areaSqm / 10000, [areaSqm]);
  const areaAcres = useMemo(() => areaSqm / 4046.8564224, [areaSqm]);

  useEffect(() => {
    setReady(true);
    return () => stopTracking();
  }, []);

  useEffect(() => {
    
    appContext.setSelectedCornerPoints(points);
    appContext.setSelectedAreaSqm(areaSqm);
    appContext.setSelectedPerimeterMeters(perimeterMeters);
    appContext.setPointsMappingMethod(mappingMethod);
    appContext.setPointsCrsMetadata(pointsCrsMetadata);
    appContext.setOriginalCornerPoints(
      mappingMethod === MANUAL_IMPORTATION ? manualOriginalPoints : []
    );

    if (callback) {
      callback({
        corners: points,
        isValid: points.length >= 2,
        areaSqm,
        perimeterMeters,
      });
    }
  }, [
    points,
    areaSqm,
    perimeterMeters,
    mappingMethod,
    pointsCrsMetadata,
    manualOriginalPoints,
  ]);

  const configuredGpsAccuracy = Number(appContext.systemParams?.maxGpsAccuracyMeters);
  const maxGpsAccuracyMeters = (
    Number.isFinite(configuredGpsAccuracy) && configuredGpsAccuracy > 0
      ? configuredGpsAccuracy
      : DEFAULT_MAX_GPS_ACCURACY_METERS
  );
  const currentPositionAlreadySelected = isGpsPointAlreadySelected(currentPosition);

  const markDisabled =
    !tracking ||
    !currentPosition ||
    accuracy === null ||
    accuracy > maxGpsAccuracyMeters ||
    currentPositionAlreadySelected;

  const canSubmit = (
    points.length >= 2 &&
    (mappingMethod === MOBILE_GPS_MAPPING || manualPointsValid)
  );

  const mapCenter =
    currentPosition || (points.length > 0 ? points[points.length - 1] : DEFAULT_CENTER);

  const alertClass =
    messageType === "error"
      ? "alert alert-danger"
      : messageType === "info"
        ? "alert alert-info"
        : "alert alert-warning";

  if (!ready) return null;

  return (
    <div className="card">
      <div className="card-body">
        <h6 className="text-center text-muted" style={{ fontSize: "22px" }}>
          <MdLocationPin size={36} /> {title || "Land Mapper"}
        </h6>

        <div className="mb-4">
          <label className="form-label font-semi-bold">
            {STR_MAPPING_METHOD[appContext.language]}
          </label>
          <div className="row">
            <div className="col-6">
              <button
                className={`btn w-100 ${mappingMethod === MOBILE_GPS_MAPPING ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => changeMappingMethod(MOBILE_GPS_MAPPING)}
              >
                {STR_MOBILE_GPS_MAPPING[appContext.language]}
              </button>
            </div>
            <div className="col-6">
              <button
                className={`btn w-100 ${mappingMethod === MANUAL_IMPORTATION ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => changeMappingMethod(MANUAL_IMPORTATION)}
              >
                {STR_MANUAL_IMPORTATION[appContext.language]}
              </button>
            </div>
          </div>
        </div>

        {
          mappingMethod === MOBILE_GPS_MAPPING
            ? <>
              {message ? (
                <div className={`${alertClass} d-flex justify-content-between align-items-center`}>
                  <div>
                    <MdWarning style={{ marginRight: "6px" }} />
                    {message}
                  </div>
                  <button className="btn btn-sm btn-outline-dark" onClick={dismissMessage}>
                    Dismiss
                  </button>
                </div>
              ) : null}

              <div style={{ height: "400px", width: "100%", borderRadius: "8px", overflow: "hidden" }}>
                <MapContainer center={mapCenter} zoom={18} style={{ height: "100%", width: "100%" }}>
                  <RecenterMap position={currentPosition} zoom={18} />

                  {satellite ? (
                    <TileLayer
                      attribution="© ESRI"
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    />
                  ) : (
                    <TileLayer
                      attribution="© OpenStreetMap"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                  )}

                  {currentPosition ? (
                    <Marker position={[currentPosition.lat, currentPosition.lng]} />
                  ) : null}

                  {points.map((point, index) => (
                    <Marker key={index} position={[point.lat, point.lng]} />
                  ))}

                  {points.length === 2 ? (
                    <Polyline positions={points.map((point) => [point.lat, point.lng])} />
                  ) : null}

                  {points.length >= 3 ? (
                    <Polygon positions={points.map((point) => [point.lat, point.lng])} />
                  ) : null}
                </MapContainer>
              </div>

              <div className="row mt-3">
                <div className="col-6 mb-2">
                  {!tracking ? (
                    <button className="btn btn-sm btn-success w-100" onClick={startTracking}>
                      Start GPS
                    </button>
                  ) : (
                    <button className="btn btn-sm btn-warning w-100" onClick={stopTracking}>
                      Stop GPS
                    </button>
                  )}
                </div>

                <div className="col-6 mb-2">
                  <button
                    className="btn btn-sm btn-primary w-100"
                    onClick={markCorner}
                    disabled={markDisabled}
                  >
                    {currentPositionAlreadySelected
                      ? "Point already selected"
                      : `Mark Point ${accuracy !== null ? `(≤${maxGpsAccuracyMeters}m)` : ""}`}
                  </button>
                </div>

                <div className="col-4">
                  <button
                    className="btn btn-sm btn-outline-secondary w-100"
                    onClick={undo}
                    disabled={historyRef.current.length === 0}
                  >
                    Undo
                  </button>
                </div>

                <div className="col-4">
                  <button
                    className="btn btn-sm btn-outline-secondary w-100"
                    onClick={redo}
                    disabled={redoRef.current.length === 0}
                  >
                    Redo
                  </button>
                </div>

                <div className="col-4">
                  <button
                    className="btn btn-sm btn-outline-danger w-100"
                    onClick={clearAll}
                    disabled={points.length === 0}
                  >
                    Clear
                  </button>
                </div>

                <div className="col-12 mt-2">
                  <button className="btn btn-sm btn-outline-dark w-100" onClick={() => setSatellite((value) => !value)}>
                    {satellite ? (
                      <>
                        <MdMap style={{ marginRight: "6px" }} />
                        Switch to Map
                      </>
                    ) : (
                      <>
                        <MdSatelliteAlt style={{ marginRight: "6px" }} />
                        Switch to Satellite
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
            : <div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    {STR_COORDINATE_DATUM[appContext.language]}
                  </label>
                  <select
                    className="form-control"
                    value={manualDatum}
                    onChange={(event) => setManualDatum(event.target.value)}
                  >
                    <option value={WGS84_DATUM}>WGS 84 (EPSG:4326)</option>
                    <option value={ARC1960_DATUM}>Arc 1960 (EPSG:4210)</option>
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    {STR_ORIGINAL_UTM_ZONE[appContext.language]}
                  </label>
                  <select
                    className="form-control"
                    value={originalUtmZone}
                    onChange={(event) => setOriginalUtmZone(event.target.value)}
                  >
                    <option value="">Not specified</option>
                    <option value="35S">35S</option>
                    <option value="36S">36S</option>
                    <option value="37S">37S</option>
                  </select>
                </div>

                <div className="col-12 mb-3">
                  <label className="form-label">
                    {STR_SOURCE_REFERENCE[appContext.language]}
                  </label>
                  <input
                    className="form-control"
                    value={sourceReference}
                    onChange={(event) => setSourceReference(event.target.value)}
                    placeholder="Example: Survey plan ABC-123"
                  />
                </div>
              </div>

              <div className="alert alert-info" style={{ fontSize: "13px" }}>
                {STR_DECIMAL_COORDINATE_HELP[appContext.language]}
              </div>

              {
                manualDatum === ARC1960_DATUM
                  ? <div className="alert alert-warning" style={{ fontSize: "13px" }}>
                    {STR_ARC1960_WARNING[appContext.language]}
                  </div>
                  : ""
              }

              {manualPointInputs.map((point, index) => (
                <div className="card mb-3" key={point.id}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-3">
                      <h6 className="font-semi-bold mNoMargin">Point #{index + 1}</h6>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeManualPoint(point.id)}
                      >
                        <MdDelete className="me-1" />
                        {STR_REMOVE[appContext.language]}
                      </button>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <label className="form-label">{STR_LATITUDE[appContext.language]}</label>
                        <input
                          className="form-control"
                          type="number"
                          step="any"
                          value={point.lat}
                          onChange={(event) => updateManualPoint(point.id, "lat", event.target.value)}
                          placeholder="-6.8340641"
                        />
                      </div>
                      <div className="col-md-6 mb-2">
                        <label className="form-label">{STR_LONGITUDE[appContext.language]}</label>
                        <input
                          className="form-control"
                          type="number"
                          step="any"
                          value={point.lng}
                          onChange={(event) => updateManualPoint(point.id, "lng", event.target.value)}
                          placeholder="39.1997098"
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mt-2">
                      <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        disabled={index === 0}
                        onClick={() => moveManualPoint(index, -1)}
                      >
                        <MdArrowUpward className="me-1" />
                        {STR_MOVE_UP[appContext.language]}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={index === manualPointInputs.length - 1}
                        onClick={() => moveManualPoint(index, 1)}
                      >
                        <MdArrowDownward className="me-1" />
                        {STR_MOVE_DOWN[appContext.language]}
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button className="btn btn-outline-primary w-100 mb-4" onClick={addManualPoint}>
                <MdAdd className="me-1" />
                {STR_ADD_POINT[appContext.language]}
              </button>

              {
                points.length > 0
                  ? <InquiryGeometryMap points={points} />
                  : <div className="alert alert-light text-center">
                    {STR_MANUAL_POINTS_INVALID[appContext.language]}
                  </div>
              }
            </div>
        }

        <div className="mt-3 text-muted" style={{ fontSize: "12px" }}>
          {mappingMethod === MANUAL_IMPORTATION || currentPosition ? (
            <>
              {mappingMethod === MOBILE_GPS_MAPPING ? (
                <>
                  Current: {currentPosition.lat}, {currentPosition.lng}
                  <br />
                  Accuracy: {accuracy !== null ? `±${accuracy} m` : "…"}
                  <br />
                </>
              ) : null}
              {points.length === 2
                ? `Line length: ${perimeterMeters.toFixed(2)} m`
                : `Perimeter: ${perimeterMeters.toFixed(2)} m`}
              <br />
              {points.length >= 3
                ? `Area: ${areaSqm.toFixed(2)} m² (${areaHectares.toFixed(4)} ha / ${areaAcres.toFixed(4)} acres)`
                : "Area: 0 m²"}
              <br />
              Points: {points.length} {
                points.length === 2
                  ? "(polyline valid)"
                  : points.length >= 3
                    ? "(polygon valid)"
                    : "(need ≥2)"
              }
            </>
          ) : (
            "Waiting for GPS signal…"
          )}
        </div>

        {
          points.length > 0
            ? <div className="mt-4">
              <CoordinateCards
                points={points}
                sourceLabel={
                  mappingMethod === MANUAL_IMPORTATION
                    ? `${STR_MANUAL_IMPORTATION[appContext.language]} · WGS 84 normalized`
                    : STR_MOBILE_GPS_MAPPING[appContext.language]
                }
              />
            </div>
            : ""
        }

        <div className="mt-3">
          {
            canSubmit ?
            <button
              className="btn btn-block btn-padded btn-rounded btn-dark"
              style={{
                fontSize: "14px"
              }}
              onClick={() => {
                appContext.navTo({
                  item: 'side-view',
                  subItem: 'land-inquiry-sender'
                })
              }}
            >
              Send Inquiry For The Selected Area / Line
            </button>
            :
            <h6
              style={{
                fontSize: "14px",
                padding: "15px",
              }}
              className="font-semi-bold mShadow1 text-center bg-primary"
            >
              <TiWarning
                size={32}
              />
              <br/>
              {
                mappingMethod === MANUAL_IMPORTATION
                  ? STR_MANUAL_POINTS_INVALID[appContext.language]
                  : "Mark at least 2 points to submit"
              }
            </h6>
          }
        </div>
      </div>
    </div>
  );
}
